import {
  Alert,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { STATUS } from "@/constants";
import type { UseAddToAppTargetResult } from "@/features/app-studio/hooks/useAddToAppTarget";

type AddToAppTargetSelectProps = {
  /** Unique prefix for this dialog's field ids (e.g. `add-table`). */
  idPrefix: string;
  target: UseAddToAppTargetResult;
  disabled?: boolean;
  /** Called after the app or tab selection changes, so the dialog can clear its save error. */
  onTargetChange?: () => void;
};

/**
 * App picker plus, when the selected app has tabs, the tab the widget should be
 * added to. Apps without tabs show no tab picker — their widgets go to the
 * implicit default tab.
 */
export function AddToAppTargetSelect({
  idPrefix,
  target,
  disabled = false,
  onTargetChange,
}: AddToAppTargetSelectProps) {
  const { t } = useTranslation("appStudio");
  const {
    apps,
    appsStatus,
    selectedAppId,
    selectApp,
    tabs,
    tabsStatus,
    tabsError,
    selectedTabId,
    selectTab,
    requiresTab,
  } = target;

  const appLabelId = `${idPrefix}-app-label`;
  const tabLabelId = `${idPrefix}-tab-label`;
  const isLoadingTabs = selectedAppId !== "" && tabsStatus === STATUS.LOADING;
  const needsTabChoice = requiresTab && selectedTabId === "";

  return (
    <Stack spacing={2}>
      <FormControl fullWidth disabled={disabled}>
        <InputLabel id={appLabelId}>{t("addToApp.appLabel")}</InputLabel>
        <Select
          labelId={appLabelId}
          label={t("addToApp.appLabel")}
          value={selectedAppId}
          onChange={(event) => {
            selectApp(event.target.value as number | "");
            onTargetChange?.();
          }}
        >
          {apps.map((app) => (
            <MenuItem key={app.id} value={app.id}>
              {app.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {isLoadingTabs ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="body2" sx={{ color: "var(--theme-text-secondary)" }}>
            {t("addToApp.loadingTabs")}
          </Typography>
        </Stack>
      ) : null}

      {requiresTab ? (
        <FormControl fullWidth disabled={disabled}>
          <InputLabel id={tabLabelId}>{t("addToApp.tabLabel")}</InputLabel>
          <Select
            labelId={tabLabelId}
            label={t("addToApp.tabLabel")}
            value={selectedTabId}
            onChange={(event) => {
              selectTab(event.target.value as number | "");
              onTargetChange?.();
            }}
          >
            {tabs.map((tab) => (
              <MenuItem key={tab.id} value={tab.id}>
                {tab.name}
              </MenuItem>
            ))}
          </Select>
          {needsTabChoice ? (
            <FormHelperText>{t("addToApp.selectTabHint")}</FormHelperText>
          ) : null}
        </FormControl>
      ) : null}

      {tabsStatus === STATUS.FAILED && tabsError ? (
        <Alert severity="error">{tabsError}</Alert>
      ) : null}

      {appsStatus === STATUS.SUCCEEDED && apps.length === 0 ? (
        <Alert severity="info">{t("addToApp.noApps")}</Alert>
      ) : null}
    </Stack>
  );
}
